/**
 * How a gateway `Notice` on the trading stream is shown (launch audit B2). Every Warn and Error
 * notice reaches the user, whatever its code: an unknown code is a toast titled after the code with
 * the gateway's text. Error notices stay until dismissed. Info notices are shown only for the
 * codes the order flow reports on (bracket protected, reverse done, …); the rest are state
 * signals (e.g. `POSITIONS_CONFIRMED`) and stay silent.
 *
 * Pure (no protobuf runtime), so it runs under `node --test`.
 */

/** Proto `NoticeLevel`. */
export const NOTICE_LEVEL = { INFO: 1, WARN: 2, ERROR: 3 } as const;

export type NoticeToast = {
  tone: "info" | "warn" | "danger" | "success";
  title: string;
  body: string;
  /** Announced through the assertive live region. */
  assertive: boolean;
  /** Stays until dismissed (`ttlMs` ignored). */
  persist: boolean;
  ttlMs: number;
};

/** Titles for codes the user sees often; anything else is titled from the code itself. */
const TITLES: Readonly<Record<string, string>> = {
  POSITIONS_UNCONFIRMED: "Positions unconfirmed — check R|Trader",
  POSITION_CORRECTED: "Position corrected from Rithmic",
  POSITION_MISMATCH: "Position mismatch",
  RISK_LIQUIDATION_BLOCKED: "Risk liquidation blocked",
  // HELM's own automatic limits (legacy engine): never Rithmic values (ADR-0045).
  RISK: "TradePrizm limit · TradePrizm-calculated",
  RISK_LIQUIDATE: "TradePrizm limit hit · TradePrizm-calculated",
  ORDER_STATE_UNKNOWN: "Order state unknown",
  RITHMIC_REJECT: "Rejected by Rithmic",
  OVERFILL: "Overfill",
  ORDER_ANOMALY: "Order anomaly",
  FLATTEN_WAITING: "Flatten waiting",
  FLATTEN_INCOMPLETE: "Flatten incomplete",
  RISK_REALIZED_ONLY: "Risk: realized P&L only",
  RISK_NOT_LOADED: "Risk state not loaded",
  RISK_STATE_NOT_SAVED: "Risk state not saved",
  CANCEL_ALL_FAILED: "Cancel all failed",
  MODIFY_UNCONFIRMED: "Modify unconfirmed",
  BRACKET_UNPROTECTED: "Bracket unprotected",
  // ADR-0059.
  RESYNC_DONE: "Resync with Rithmic",
  ORDER_SETTLED: "Order settled from Rithmic",
  BRACKET_PARTIAL: "Bracket covers the filled part",
};

/** Codes the order flow toasts at any level, Info included (the pre-B2 allowlist). */
const INFO_CODES: ReadonlySet<string> = new Set([
  "FLATTEN_INCOMPLETE",
  "ORPHAN_FILL",
  "POSITION_MISMATCH",
  "RISK_LIQUIDATE",
  "RISK",
  // Brackets managed by the gateway (ADR-0025).
  "BRACKET_UNPROTECTED",
  "BRACKET_PROTECTED",
  "BRACKET_LEG_CANCELLED",
  "BRACKET_LEG_REJECTED",
  "BRACKET_LEG_NOT_PLACED",
  "BRACKET_RESIZE_FALLBACK",
  "BRACKET_UNVERIFIED",
  "BRACKET_STATE_NOT_SAVED",
  "BRACKET_STATE_NOT_LOADED",
  "POSITION_RESTORED",
  // Reverse & Cxl (SPEC §9.7).
  "REVERSE_DONE",
  "REVERSE_ABORTED",
  "REVERSE_FAILED",
  "REVERSE_INCOMPLETE",
  // "Resync with Rithmic" (ADR-0059): its outcome is the answer to the owner's click.
  "RESYNC_DONE",
]);

/** "SOME_CODE" → "Some code"; an empty code → "Gateway notice". */
export function noticeTitle(code: string): string {
  const known = TITLES[code];
  if (known !== undefined) return known;
  const words = code.replace(/_/g, " ").trim().toLowerCase();
  return words === "" ? "Gateway notice" : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The toast for a gateway notice, or null when it is not shown. `replayed`: the frame predates
 * this page (replayed from the gateway's ring on first connect) — still shown, but it expires
 * like any toast instead of piling up persistent errors on every reload.
 */
export function noticeToast(
  n: { level: number; code: string; text: string },
  replayed = false,
): NoticeToast | null {
  const level = n.level;
  if (level < NOTICE_LEVEL.WARN && !INFO_CODES.has(n.code)) return null;
  // A held exit (ADR-0050) is the persistent FLATTEN_WAITING banner with its Force button, not a toast.
  if (n.code === "FLATTEN_WAITING" && level >= NOTICE_LEVEL.ERROR) return null;
  const error = level >= NOTICE_LEVEL.ERROR;
  return {
    tone: error ? "danger" : level === NOTICE_LEVEL.WARN ? "warn" : "success",
    title: noticeTitle(n.code),
    body: n.text || n.code,
    assertive: error,
    persist: error && !replayed,
    ttlMs: level >= NOTICE_LEVEL.WARN ? 12_000 : 5_000,
  };
}
